import { createPdfComposer } from "../../_CORE/APP/Templates/_UI/Pdf/PdfComposer";
import { requestPdfExportAppearance } from "../../_CORE/APP/Templates/_UI/Pdf/PdfExportAppearance";

const onDownloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const safeDocumentName = (value = "documento") => String(value || "documento")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/(^-+|-+$)/g, "") || "documento";

const onMarkdownDocument = (title, content = "") => `# ${String(title || "Documento").trim()}\n\n${String(content || "").trim()}\n`;

export const downloadMarkdownDocument = ({ title, content = "", fileName = null }) => {
    const name = fileName || `${safeDocumentName(title)}.md`;
    onDownloadBlob(new Blob([onMarkdownDocument(title, content)], { type: "text/markdown;charset=utf-8" }), name);
};

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[n] = c >>> 0;
    }
    return table;
})();

const onCrc32 = (bytes) => {
    let crc = 0xFFFFFFFF;
    for (let index = 0; index < bytes.length; index += 1) crc = CRC_TABLE[(crc ^ bytes[index]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
};

const onDosDateTime = (date = new Date()) => {
    const year = Math.max(1980, date.getFullYear());
    const dosTime = ((date.getHours() & 0x1F) << 11) | ((date.getMinutes() & 0x3F) << 5) | ((Math.floor(date.getSeconds() / 2)) & 0x1F);
    const dosDate = (((year - 1980) & 0x7F) << 9) | (((date.getMonth() + 1) & 0x0F) << 5) | (date.getDate() & 0x1F);
    return { dosDate, dosTime };
};

const onU16 = (value) => {
    const bytes = new Uint8Array(2);
    new DataView(bytes.buffer).setUint16(0, value, true);
    return bytes;
};

const onU32 = (value) => {
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setUint32(0, value >>> 0, true);
    return bytes;
};

const onJoinBytes = (parts) => {
    const size = parts.reduce((total, part) => total + part.length, 0);
    const output = new Uint8Array(size);
    let offset = 0;
    parts.forEach((part) => { output.set(part, offset); offset += part.length; });
    return output;
};

const onCreateStoredZip = (entries) => {
    const encoder = new TextEncoder();
    const localParts = [];
    const centralParts = [];
    const { dosDate, dosTime } = onDosDateTime();
    let localOffset = 0;

    entries.forEach((entry) => {
        const nameBytes = encoder.encode(entry.path.replace(/\\/g, "/"));
        const dataBytes = encoder.encode(entry.content || "");
        const crc = onCrc32(dataBytes);
        const localHeader = onJoinBytes([
            onU32(0x04034B50), onU16(20), onU16(0x0800), onU16(0), onU16(dosTime), onU16(dosDate),
            onU32(crc), onU32(dataBytes.length), onU32(dataBytes.length), onU16(nameBytes.length), onU16(0), nameBytes,
        ]);
        localParts.push(localHeader, dataBytes);

        const centralHeader = onJoinBytes([
            onU32(0x02014B50), onU16(20), onU16(20), onU16(0x0800), onU16(0), onU16(dosTime), onU16(dosDate),
            onU32(crc), onU32(dataBytes.length), onU32(dataBytes.length), onU16(nameBytes.length), onU16(0), onU16(0),
            onU16(0), onU16(0), onU32(0), onU32(localOffset), nameBytes,
        ]);
        centralParts.push(centralHeader);
        localOffset += localHeader.length + dataBytes.length;
    });

    const central = onJoinBytes(centralParts);
    const end = onJoinBytes([
        onU32(0x06054B50), onU16(0), onU16(0), onU16(entries.length), onU16(entries.length),
        onU32(central.length), onU32(localOffset), onU16(0),
    ]);
    return onJoinBytes([...localParts, central, end]);
};

const onUniqueSegment = (raw, used) => {
    const base = safeDocumentName(raw || "pagina");
    const key = base.toLowerCase();
    const count = (used.get(key) || 0) + 1;
    used.set(key, count);
    return count === 1 ? base : `${base}-${count}`;
};

const onTreeMarkdownEntries = (tree, rootPath) => {
    const entries = [];
    const walk = (nodes, parentPath) => {
        const used = new Map();
        (nodes || []).forEach((node) => {
            const segment = onUniqueSegment(node.title || "pagina", used);
            const nodePath = `${parentPath}/${segment}`;
            entries.push({ path: `${nodePath}/${segment}.md`, content: onMarkdownDocument(node.title || "Pagina", node.content || "") });
            walk(node.children || [], nodePath);
        });
    };
    walk(tree || [], rootPath);
    return entries;
};

export const downloadMarkdownTreeZip = ({ title, description = "", tree = [], fileName = null }) => {
    const root = safeDocumentName(title || "documentacion");
    const intro = [`# ${title || "Documentacion"}`, ""];
    if (String(description || "").trim()) intro.push(String(description).trim(), "");
    intro.push("Cada pagina se exporta en su propia carpeta y archivo Markdown, respetando la jerarquia del documento.", "");
    const entries = [
        { path: `${root}/README.md`, content: intro.join("\n") },
        ...onTreeMarkdownEntries(tree, root),
    ];
    const bytes = onCreateStoredZip(entries);
    onDownloadBlob(new Blob([bytes], { type: "application/zip" }), fileName || `${root}-markdown.zip`);
};

const onPlainMarkdown = (value) => String(value || "")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "[Imagen: $1]")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)")
    .replace(/<[^>]+>/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^>\s?/g, "")
    .trimEnd();

const onMarkdownBlocks = (markdown = "") => {
    const blocks = [];
    let inCode = false;
    String(markdown || "").replace(/\r/g, "").split("\n").forEach((raw) => {
        const line = raw ?? "";
        if (/^```/.test(line.trim())) { inCode = !inCode; return; }
        if (inCode) { blocks.push({ text: line || " ", font: "mono", size: 8.7, indent: 12, after: 1 }); return; }
        const heading = line.match(/^(#{1,6})\s+(.+)$/);
        if (heading) {
            blocks.push({ text: onPlainMarkdown(heading[2]), font: "bold", size: Math.max(11, 17 - ((heading[1].length - 1) * 1.4)), before: 5, after: 4, underline: true });
            return;
        }
        if (/^\s*([-*_])(?:\s*\1){2,}\s*$/.test(line)) { blocks.push({ rule: true, after: 6 }); return; }
        const field = line.match(/^\s*\*\*([^*]{1,60}?):\*\*\s*(.*)$/);
        if (field) { blocks.push({ label: onPlainMarkdown(field[1]), value: onPlainMarkdown(field[2]), after: 2 }); return; }
        const checkbox = line.match(/^\s*[-*+]\s+\[([ xX])\]\s+(.+)$/);
        if (checkbox) { blocks.push({ text: `${checkbox[1].toLowerCase() === "x" ? "[x]" : "[ ]"} ${onPlainMarkdown(checkbox[2])}`, indent: 12, size: 9.5, after: 2 }); return; }
        const bullet = line.match(/^\s*[-*+]\s+(.+)$/);
        if (bullet) { blocks.push({ text: `- ${onPlainMarkdown(bullet[1])}`, indent: 12, size: 9.5, after: 2 }); return; }
        const ordered = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
        if (ordered) { blocks.push({ text: `${ordered[1]}. ${onPlainMarkdown(ordered[2])}`, indent: 12, size: 9.5, after: 2 }); return; }
        blocks.push({ text: onPlainMarkdown(line), size: 9.5, after: line.trim() ? 2 : 4 });
    });
    return blocks;
};

const onApplyBlocks = (pdf, blocks, extraIndent = 0) => {
    blocks.forEach((block) => {
        if (block.rule) {
            pdf.addRule({ color: "#d0d0d0", before: 2, after: block.after || 6, width: 0.5 });
            return;
        }
        if (block.label) {
            pdf.addLabelValue(block.label, block.value);
            if (block.after) pdf.addSpacer(block.after);
            return;
        }
        pdf.addText({ ...block, indent: (block.indent || 0) + extraIndent });
    });
};

export const downloadPdfDocument = async ({ title, content = "", fileName = null, appearance = null }) => {
    const style = appearance || await requestPdfExportAppearance({ title: title || "Documento" });
    if (!style) return false;
    const pdf = createPdfComposer({ appearance: style, documentTitle: title || "Documento" });
    pdf.addTitle(title || "Documento");
    pdf.addMeta(`Documento exportado · ${new Date().toLocaleString("es-AR")}`);
    pdf.addRule({ after: 10 });
    onApplyBlocks(pdf, onMarkdownBlocks(content));
    const bytes = pdf.finish();
    onDownloadBlob(new Blob([bytes], { type: "application/pdf" }), fileName || `${safeDocumentName(title)}.pdf`);
    return true;
};

export const downloadPdfTree = async ({ title, description = "", tree = [], fileName = null, appearance = null }) => {
    const style = appearance || await requestPdfExportAppearance({ title: title || "Documentacion" });
    if (!style) return false;
    const pdf = createPdfComposer({ appearance: style, documentTitle: title || "Documentacion" });
    pdf.addTitle(title || "Documentacion");
    if (String(description || "").trim()) pdf.addText({ text: String(description).trim(), size: 10.5, after: 8 });
    pdf.addMeta(`${tree.length} secciones principales · ${new Date().toLocaleString("es-AR")}`);
    pdf.addRule({ after: 10 });

    const walk = (nodes, depth = 0) => {
        (nodes || []).forEach((node) => {
            const indent = Math.min(depth * 10, 36);
            pdf.addText({
                text: node.title || "Pagina",
                font: "bold",
                size: Math.max(12, 18 - (depth * 1.7)),
                indent,
                before: depth === 0 ? 8 : 5,
                after: 5,
                underline: true,
            });
            onApplyBlocks(pdf, onMarkdownBlocks(node.content || ""), indent);
            pdf.addSpacer(5);
            walk(node.children || [], depth + 1);
        });
    };
    walk(tree);

    const bytes = pdf.finish();
    onDownloadBlob(new Blob([bytes], { type: "application/pdf" }), fileName || `${safeDocumentName(title || "documentacion")}.pdf`);
    return true;
};
